/**
 * The `Grid` catalog component: figures down the left, what is being compared
 * across the top, the reader's own column marked.
 *
 * 13c is the shape — "Compare NABIL and SANIMA" answered with Price, P/E,
 * dividend and NPL as rows and a column per bank, `YOU HOLD` written under the
 * one the reader owns. It is the catalog's answer to the plain table, so the
 * catch-all result draws the way the design draws it rather than as a
 * spreadsheet.
 *
 * TRANSPOSED, AND THAT IS THE POINT. A tool returns a row per company; drawing
 * that directly gives a company per line with a dozen columns running off the
 * edge of a 375px phone. The reader is comparing figures, so the figures are
 * the rows and the companies are the columns on a wide panel. In the narrow
 * rail the same figures become one record per metric so labels and long
 * figures stay visible without horizontal panning.
 *
 * The marked column is 13j's rule again: "your holding is always marked". The
 * mark arrives on the wire from the row's own `held`; nothing here infers it.
 *
 * A missing value draws an em dash. Never a blank, which reads as a column
 * that stopped, and never a zero, which reads as a figure.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { CellValue } from '@/features/mitra/lib/figures'
import { captionLabel, humanLabel } from '@/features/mitra/lib/figures'
import { useLabelLang } from '@/features/mitra/panel/blocks/charts/labelLang'
import { strongerIndex, strongerLegend } from '@/features/mitra/lib/stronger'
import { Fig, Label, SCROLL_X } from '@/features/mitra/panel/blocks/Card'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const GridApi = {
  name: 'Grid',
  schema: z
    .object({
      columns: PathBinding,
      figures: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/columns`, fixed by `to_messages`. */
export type ColumnPart = { id: string; label: string; note: string | null }
/** The shape behind `/figures`: one figure, its value in each column. */
export type GridRowPart = {
  id: string
  label: string
  values: (CellValue | null)[]
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const gridApi = GridApi as unknown as ComponentApi

export const Grid = createComponentImplementation<ComponentApi>(
  gridApi,
  ({ props }: { props: { columns?: unknown; figures?: unknown; caption?: unknown } }) => (
    <GridTable
      columns={listOf<ColumnPart>(props.columns)}
      figures={listOf<GridRowPart>(props.figures)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

//: What a row is called, and the floor it may not be squeezed below. Fixed
//: rather than `flex-1`: the label was the only flexible child in a row of
//: fixed columns, so a table one column too wide resolved it to zero and the
//: reader was left with rows of figures naming nothing. Wide enough for
//: "Unrealised gain" to truncate rather than disappear.
const GRID_LABEL = 'w-26 flex-none'

/** The drawing, given the two lists the surface resolved. */
export function GridTable({
  columns,
  figures,
  caption,
}: {
  columns: ColumnPart[]
  figures: GridRowPart[]
  caption: string | null
}) {
  const lang = useLabelLang()
  if (columns.length === 0 || figures.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }

  const legend = strongerLegend(figures.map((row) => row.id))
  return (
    <figure
      data-role="chart"
      data-component="grid"
      className="agent-grid m-0 flex flex-col gap-2 px-3 pb-3 pt-3"
    >
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}

      {/* ONE PANNABLE REGION, HEADER AND ROWS TOGETHER. Reported from the
          running widget: a four-company grid drew a fourth column cut through
          by the card's edge and no row labels at all. Both came from the same
          line — the label was the only flexible child beside `w-[88px]
          flex-none` columns, so once the fixed columns outgrew the card flex
          resolved it to ZERO and the row still overflowed. The labels did not
          truncate, they vanished, and the last figure was sliced mid-glyph:
          the "partial number, not a tight one" failure `tableShape` was
          written for, in the one shape that never adopted it.

          Two scrollers would slide the headings out of step with their own
          figures, which is a worse table than a clipped one — so the head and
          every row sit in this one box. `SCROLL_X` and not `overflow-x-auto`,
          because that alone scrolls both axes and has already drawn a vertical
          scrollbar beside a price. */}
      <div data-role="grid-scroll" className={SCROLL_X}>
        <div data-role="grid-head" className="flex items-end gap-2">
          <span className={`${GRID_LABEL} sticky left-0 z-10 bg-surface`} />
          {columns.map((c) => (
            <span
              key={c.id}
              data-role="grid-column"
              data-mine={c.note ? 'true' : 'false'}
              className="flex min-w-22 flex-none flex-col items-center gap-0"
            >
              <span className="truncate font-mono text-body-xs font-bold text-ink">{c.label}</span>
              {/* The em dash is drawn rather than left blank, so the column
                that is NOT yours says so as plainly as the one that is. */}
              <span
                className={`font-mono text-mono-s tracking-[0.08em]
                              ${c.note ? 'font-semibold text-ink-mid' : 'text-ink-soft'}`}
              >
                {c.note ?? '—'}
              </span>
            </span>
          ))}
        </div>

        {figures.map((row) => (
          <div
            key={row.id}
            data-role="grid-row"
            className="flex items-center gap-2 border-t border-line pt-2"
          >
            <span
              data-role="grid-row-label"
              className={`${GRID_LABEL} sticky left-0 z-10 bg-surface pr-2 text-label-m text-ink-soft`}
            >
              {humanLabel(row.label, lang)}
            </span>
            {columns.map((c, i) => (
              <span
                key={c.id}
                data-role="grid-cell"
                // GREEN MARKS A FACT, NOT A VERDICT. Design 13c marks one figure
                // per row and captions it "Green marks the better number";
                // "better" is a judgement this engine does not make, and "lower"
                // is not — a P/E is price over earnings, so the lower one is
                // fewer rupees paid per rupee earned. `strongerIndex` marks only
                // measures with a definitional direction and never price, and
                // the legend below says which way each one runs.
                data-strong={strongerIndex(row.id, row.values) === i ? 'true' : undefined}
                // A FLOOR, NOT A WIDTH. Captured from the running widget: a
                // market cap drew as `NPR 1,46,16,18`, cut off mid-number by an
                // 88px cell. `Card`'s `tableShape` names this exact failure —
                // "THE FAILURE IS A PARTIAL NUMBER, NOT A TIGHT ONE ... a
                // truncated figure ending in a decimal point reads as a figure
                // rather than as a fold." The cell panned, so nothing was lost,
                // and at rest the reader was looking at a number that is not the
                // number.
                //
                // So a long figure widens its own column and the whole grid pans
                // as one region, which it already does. The header carries the
                // same floor: two width rules would slide the headings off their
                // own figures.
                className={`min-w-22 flex-none px-1 text-center ${SCROLL_X}`}
              >
                {row.values[i] == null ? (
                  <span className="font-mono text-body-s text-ink-soft">—</span>
                ) : (
                  <Fig
                    cell={row.values[i] as CellValue}
                    quietUnit
                    className={`text-body-s font-bold tabular-nums ${
                      strongerIndex(row.id, row.values) === i ? 'text-up' : ''
                    }`}
                  />
                )}
              </span>
            ))}
          </div>
        ))}
      </div>
      <div data-role="grid-ledger" className="flex flex-col gap-2">
        {figures.map((row) => {
          const strong = strongerIndex(row.id, row.values)
          return (
            <section
              key={row.id}
              data-role="grid-ledger-row"
              className="rounded-block border border-rule bg-ground/50 p-2"
            >
              <h3 className="mb-2 text-body-xs font-semibold text-ink">
                {humanLabel(row.label, lang)}
              </h3>
              <dl className="grid grid-cols-1 gap-y-2">
                {columns.map((column, i) => (
                  <div key={column.id} className="min-w-0 border-t border-rule pt-2">
                    <dt className="flex flex-wrap items-baseline gap-x-2 text-mono-s text-ink-soft">
                      <span className="font-semibold text-ink-mid">{column.label}</span>
                      {column.note && (
                        <span className="font-semibold text-ink-mid">{column.note}</span>
                      )}
                    </dt>
                    <dd
                      className={`m-0 mt-0 text-body-s font-semibold tabular-nums ${SCROLL_X} ${
                        strong === i ? 'text-up' : 'text-ink'
                      }`}
                    >
                      {row.values[i] == null ? (
                        '—'
                      ) : (
                        <Fig cell={row.values[i] as CellValue} quietUnit />
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )
        })}
      </div>
      {legend && (
        <p data-role="stronger-legend" className="pt-2 text-label-s text-ink-soft">
          {legend}
        </p>
      )}
    </figure>
  )
}
